import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import Chip from 'tio-reimagined/components/chip';
import PageHero from 'tio-reimagined/components/page-hero';
import SectionHeader from 'tio-reimagined/components/section-header';
import Button from 'tio-reimagined/components/button';
import Modal from 'tio-reimagined/components/modal';
import ConfirmationDialog from 'tio-reimagined/components/confirmation-dialog';
import Select from 'tio-reimagined/components/select';
import type { SelectOption } from 'tio-reimagined/components/select';
import {
  PhArrowLeft,
  PhStack,
  PhChatCircle,
  PhTrash,
  PhWarning,
  PhCheckCircle,
  PhInfo,
  PhBell,
  PhConfetti,
  PhLightning,
  PhMagnifyingGlass,
  PhUser,
} from 'ember-phosphor-icons';
import type ReimaginedToastService from 'tio-employee/services/reimagined-toast';

// ---------------------------------------------------------------------------
// Overlays Demo Page — Modal, ConfirmationDialog, Toast
// ---------------------------------------------------------------------------

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const MOCK_EMPLOYEES: SelectOption[] = [
  { key: '1', label: 'Alice Johnson', description: 'alice@example.com · Engineering' },
  { key: '2', label: 'Bob Williams', description: 'bob@example.com · Design' },
  { key: '3', label: 'Carol Davis', description: 'carol@example.com · Product' },
  { key: '4', label: 'David Brown', description: 'david@example.com · Engineering' },
  { key: '5', label: 'Eva Martinez', description: 'eva@example.com · Marketing' },
  { key: '6', label: 'Frank Lee', description: 'frank@example.com · Sales' },
  { key: '7', label: 'Grace Kim', description: 'grace@example.com · HR' },
  { key: '8', label: 'Henry Chen', description: 'henry@example.com · Engineering' },
  { key: '9', label: 'Iris Patel', description: 'iris@example.com · Finance' },
  { key: '10', label: 'James Taylor', description: 'james@example.com · Data Science' },
];

export default class OverlaysDetailPage extends Component {
  @service('reimagined-toast') declare toast: ReimaginedToastService;

  // -- Modal state --
  @tracked isBasicModalOpen = false;
  @tracked isSmModalOpen = false;
  @tracked isLgModalOpen = false;
  @tracked isXlModalOpen = false;
  @tracked isFullModalOpen = false;
  @tracked isScrollModalOpen = false;

  // -- Confirmation Dialog state --
  @tracked isWarningConfirmOpen = false;
  @tracked isDangerConfirmOpen = false;
  @tracked isSuccessConfirmOpen = false;
  @tracked isInfoConfirmOpen = false;
  @tracked isTypeConfirmOpen = false;
  @tracked isLoadingConfirmOpen = false;
  @tracked isConfirmRunning = false;
  @tracked confirmError = '';

  // --------------- Modal handlers ---------------

  openBasicModal = () => {
    this.isBasicModalOpen = true;
  };
  closeBasicModal = () => {
    this.isBasicModalOpen = false;
  };

  openSizeModal = (size: ModalSize) => {
    if (size === 'sm') this.isSmModalOpen = true;
    if (size === 'lg') this.isLgModalOpen = true;
    if (size === 'xl') this.isXlModalOpen = true;
    if (size === 'full') this.isFullModalOpen = true;
  };

  closeSmModal = () => {
    this.isSmModalOpen = false;
  };
  closeLgModal = () => {
    this.isLgModalOpen = false;
  };
  closeXlModal = () => {
    this.isXlModalOpen = false;
  };
  closeFullModal = () => {
    this.isFullModalOpen = false;
  };

  openScrollModal = () => {
    this.isScrollModalOpen = true;
  };
  closeScrollModal = () => {
    this.isScrollModalOpen = false;
  };

  // --------------- Select-in-Modal state + handlers ---------------

  @tracked isSelectModalOpen = false;
  @tracked selectModalResults: SelectOption[] = [];
  @tracked selectModalIsLoading = false;
  @tracked selectModalSelected: string | null = null;

  openSelectModal = () => {
    this.isSelectModalOpen = true;
    this.selectModalResults = [];
    this.selectModalSelected = null;
  };

  closeSelectModal = () => {
    this.isSelectModalOpen = false;
  };

  handleSelectModalSearch = (query: string) => {
    this.selectModalIsLoading = true;
    setTimeout(() => {
      const q = query.toLowerCase();
      this.selectModalResults = MOCK_EMPLOYEES.filter(
        (e) =>
          e.label.toLowerCase().includes(q) || (e.description?.toLowerCase().includes(q) ?? false)
      );
      this.selectModalIsLoading = false;
    }, 500);
  };

  setSelectModalSelected = (key: string | null) => {
    this.selectModalSelected = key;
  };

  confirmSelectModal = () => {
    const employee = MOCK_EMPLOYEES.find((e) => e.key === this.selectModalSelected);
    if (employee) {
      this.toast.success(`Assigned to ${employee.label}`);
    }
    this.isSelectModalOpen = false;
  };

  // --------------- Confirmation handlers ---------------

  openConfirm = (intent: string) => {
    if (intent === 'warning') this.isWarningConfirmOpen = true;
    if (intent === 'danger') this.isDangerConfirmOpen = true;
    if (intent === 'success') this.isSuccessConfirmOpen = true;
    if (intent === 'info') this.isInfoConfirmOpen = true;
  };

  closeConfirm = (intent: string) => {
    if (intent === 'warning') this.isWarningConfirmOpen = false;
    if (intent === 'danger') this.isDangerConfirmOpen = false;
    if (intent === 'success') this.isSuccessConfirmOpen = false;
    if (intent === 'info') this.isInfoConfirmOpen = false;
    this.confirmError = '';
  };

  handleConfirm = (intent: string) => {
    this.toast.success(`${intent} action confirmed!`);
    this.closeConfirm(intent);
  };

  openTypeConfirm = () => {
    this.isTypeConfirmOpen = true;
  };
  closeTypeConfirm = () => {
    this.isTypeConfirmOpen = false;
    this.confirmError = '';
  };
  handleTypeConfirm = () => {
    this.toast.success('Typed confirmation accepted!');
    this.isTypeConfirmOpen = false;
  };

  openLoadingConfirm = () => {
    this.isLoadingConfirmOpen = true;
    this.confirmError = '';
  };
  closeLoadingConfirm = () => {
    this.isLoadingConfirmOpen = false;
    this.isConfirmRunning = false;
    this.confirmError = '';
  };
  handleLoadingConfirm = () => {
    this.isConfirmRunning = true;
    this.confirmError = '';
    setTimeout(() => {
      this.isConfirmRunning = false;
      this.isLoadingConfirmOpen = false;
      this.toast.success('Async operation completed!');
    }, 2000);
  };

  // --------------- Toast handlers ---------------

  fireSuccessToast = () => {
    this.toast.success('Changes saved successfully!');
  };

  fireErrorToast = () => {
    this.toast.error('Something went wrong. Please try again.');
  };

  fireWarningToast = () => {
    this.toast.warning('Your session expires in 5 minutes.');
  };

  fireInfoToast = () => {
    this.toast.info('A new version is available.');
  };

  fireActionToast = () => {
    this.toast.success('Document uploaded.', {
      actionLabel: 'View',
      onAction: () => {
        this.toast.info('Navigating to document...');
      },
    });
  };

  fireBurstToast = () => {
    this.toast.success('Deployment complete!');
    setTimeout(() => this.toast.info('All services healthy.'), 400);
    setTimeout(() => this.toast.success('Metrics look great!'), 800);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Back navigation }}
      <div class="mb-4">
        <LinkTo
          @route="authenticated.reimagined.components"
          class="inline-flex items-center gap-2 text-violet-600 hover:text-violet-800 font-medium"
        >
          <PhArrowLeft @size={{18}} />
          Back to Component Library
        </LinkTo>
      </div>

      {{! Header }}
      <PageHero
        @title="Overlays"
        @subtitle="Modal, ConfirmationDialog, and Toast — premium overlay components with glassmorphism, spring animations, and micro-interactions."
        @icon={{PhStack}}
        @theme="violet"
        @decoration="dots"
      >
        <:actions>
          <Chip @intent="primary">
            <PhLightning @size={{14}} class="mr-1" />
            3 Components
          </Chip>
          <Chip @intent="success">
            <PhCheckCircle @size={{14}} class="mr-1" />
            Zero Frontile
          </Chip>
        </:actions>
      </PageHero>

      {{! ================================================================
            SECTION 1: Modal
            ================================================================ }}
      <SectionHeader
        @title="Modal"
        @icon={{PhStack}}
        @subtitle="Full-featured dialog overlay with frosted-glass backdrop, spring entrance, focus management, and scroll lock."
      />

      <div class="space-y-6">
        {{! Basic Modal }}
        <div class="bg-white rounded-xl border border-gray-200 p-6">
          <Heading @level={{4}} class="mb-2">Basic Modal</Heading>
          <Text @intent="muted" class="mb-4">Default medium-sized modal with header, body, and
            footer blocks.</Text>
          <Button @intent="primary" {{on "click" this.openBasicModal}}>
            Open Modal
          </Button>
        </div>

        <Modal @isOpen={{this.isBasicModalOpen}} @onClose={{this.closeBasicModal}} @size="md">
          <:header>
            <Heading @level={{3}}>Welcome to Reimagined</Heading>
          </:header>
          <:body>
            <Text class="text-gray-700">
              This modal uses a frosted-glass backdrop, spring-feel entrance animation, and
              automatic scroll lock. Try pressing Escape or clicking outside to dismiss.
            </Text>
            <div class="mt-4 p-4 bg-violet-50 rounded-lg border border-violet-100">
              <Text @intent="muted" class="text-sm">
                Built entirely with Tailwind CSS — no Frontile dependency. Follows the same
                lifecycle pattern as the Drawer component.
              </Text>
            </div>
          </:body>
          <:footer>
            <div class="flex justify-end gap-3">
              <Button @appearance="outlined" {{on "click" this.closeBasicModal}}>Cancel</Button>
              <Button @intent="primary" {{on "click" this.closeBasicModal}}>Got it!</Button>
            </div>
          </:footer>
        </Modal>

        {{! Sizes }}
        <div class="bg-white rounded-xl border border-gray-200 p-6">
          <Heading @level={{4}} class="mb-2">Sizes</Heading>
          <Text @intent="muted" class="mb-4">Modal supports sm, md, lg, xl, and full sizes.</Text>
          <div class="flex flex-wrap gap-3">
            <Button
              @appearance="outlined"
              {{on "click" (fn this.openSizeModal "sm")}}
            >Small</Button>
            <Button
              @appearance="outlined"
              {{on "click" (fn this.openSizeModal "lg")}}
            >Large</Button>
            <Button @appearance="outlined" {{on "click" (fn this.openSizeModal "xl")}}>Extra Large</Button>
            <Button @appearance="outlined" {{on "click" (fn this.openSizeModal "full")}}>Full Screen</Button>
          </div>
        </div>

        <Modal @isOpen={{this.isSmModalOpen}} @onClose={{this.closeSmModal}} @size="sm">
          <:header><Heading @level={{3}}>Small Modal</Heading></:header>
          <:body>
            <Text>A compact modal for quick confirmations or short messages.</Text>
          </:body>
          <:footer>
            <div class="flex justify-end">
              <Button @intent="primary" {{on "click" this.closeSmModal}}>Close</Button>
            </div>
          </:footer>
        </Modal>

        <Modal @isOpen={{this.isLgModalOpen}} @onClose={{this.closeLgModal}} @size="lg">
          <:header><Heading @level={{3}}>Large Modal</Heading></:header>
          <:body>
            <Text>A wider modal ideal for forms, tables, or detailed content that needs more space.</Text>
            <div class="mt-4 grid grid-cols-2 gap-4">
              <div class="p-4 bg-gray-50 rounded-lg">
                <Text class="font-medium">Column 1</Text>
                <Text @intent="muted" class="text-sm">More room for side-by-side layouts.</Text>
              </div>
              <div class="p-4 bg-gray-50 rounded-lg">
                <Text class="font-medium">Column 2</Text>
                <Text @intent="muted" class="text-sm">Tables, comparison views, etc.</Text>
              </div>
            </div>
          </:body>
          <:footer>
            <div class="flex justify-end">
              <Button @intent="primary" {{on "click" this.closeLgModal}}>Close</Button>
            </div>
          </:footer>
        </Modal>

        <Modal @isOpen={{this.isXlModalOpen}} @onClose={{this.closeXlModal}} @size="xl">
          <:header><Heading @level={{3}}>Extra-Large Modal</Heading></:header>
          <:body>
            <Text>Maximum width before fullscreen. Great for data-heavy views.</Text>
          </:body>
          <:footer>
            <div class="flex justify-end">
              <Button @intent="primary" {{on "click" this.closeXlModal}}>Close</Button>
            </div>
          </:footer>
        </Modal>

        <Modal @isOpen={{this.isFullModalOpen}} @onClose={{this.closeFullModal}} @size="full">
          <:header><Heading @level={{3}}>Full-Screen Modal</Heading></:header>
          <:body>
            <Text>Takes over the entire viewport. Useful for immersive wizards or editors.</Text>
          </:body>
          <:footer>
            <div class="flex justify-end">
              <Button @intent="primary" {{on "click" this.closeFullModal}}>Close</Button>
            </div>
          </:footer>
        </Modal>

        {{! Scrollable Content }}
        <div class="bg-white rounded-xl border border-gray-200 p-6">
          <Heading @level={{4}} class="mb-2">Scrollable Content</Heading>
          <Text @intent="muted" class="mb-4">Long content scrolls inside the body while header and
            footer stay fixed.</Text>
          <Button @appearance="outlined" {{on "click" this.openScrollModal}}>Open Scroll Demo</Button>
        </div>

        <Modal @isOpen={{this.isScrollModalOpen}} @onClose={{this.closeScrollModal}} @size="md">
          <:header><Heading @level={{3}}>Scrollable Content</Heading></:header>
          <:body>
            <div class="space-y-4">
              {{#each (Array 12) as |_item idx|}}
                <div class="p-4 bg-gray-50 rounded-lg">
                  <Text class="font-medium">Section {{idx}}</Text>
                  <Text @intent="muted" class="text-sm mt-1">
                    This is a content block to demonstrate scrolling behavior. The modal body
                    scrolls independently while the header and footer remain fixed.
                  </Text>
                </div>
              {{/each}}
            </div>
          </:body>
          <:footer>
            <div class="flex justify-end">
              <Button @intent="primary" {{on "click" this.closeScrollModal}}>Done</Button>
            </div>
          </:footer>
        </Modal>

        {{! Select Inside Modal — z-index layering proof }}
        <div class="bg-white rounded-xl border border-gray-200 p-6">
          <Heading @level={{4}} class="mb-2">Select Inside Modal</Heading>
          <Text @intent="muted" class="mb-4">
            Proves that a portaled Select dropdown (z-index 9999) layers correctly above a portaled
            Modal (z-50). Both escape the layout stacking context via
            <code class="text-xs bg-gray-100 px-1 rounded">in-element</code>.
          </Text>
          <Button @appearance="outlined" {{on "click" this.openSelectModal}}>
            <PhMagnifyingGlass @size={{16}} class="mr-1" />
            Open Select-in-Modal
          </Button>
        </div>

        <Modal @isOpen={{this.isSelectModalOpen}} @onClose={{this.closeSelectModal}} @size="md">
          <:header>
            <div class="flex items-center gap-2">
              <PhUser @size={{20}} class="text-violet-500" />
              <Heading @level={{3}}>Assign Reviewer</Heading>
            </div>
          </:header>
          <:body>
            <div class="space-y-4">
              <Text @intent="muted">
                Search for an employee to assign as reviewer. The dropdown should appear
                <strong>above</strong>
                this modal — both are portaled to the document body.
              </Text>
              <Select
                @label="Find Employee"
                @items={{this.selectModalResults}}
                @searchable={{true}}
                @onSearch={{this.handleSelectModalSearch}}
                @isLoading={{this.selectModalIsLoading}}
                @selected={{this.selectModalSelected}}
                @onChange={{this.setSelectModalSelected}}
                @placeholder="Search by name or email…"
                @noResultsText="No employees found"
              >
                <:option as |opt|>
                  <div class="flex flex-col">
                    <span class="font-medium">{{opt.label}}</span>
                    {{#if opt.description}}
                      <span class="text-xs text-gray-500">{{opt.description}}</span>
                    {{/if}}
                  </div>
                </:option>
              </Select>
              {{#if this.selectModalSelected}}
                <div
                  class="flex items-center gap-2 p-3 bg-violet-50 rounded-lg border border-violet-200"
                >
                  <PhCheckCircle @size={{18}} class="text-violet-600" />
                  <Text class="text-sm text-violet-800">
                    Selected:
                    <strong>{{this.selectModalSelected}}</strong>
                  </Text>
                </div>
              {{/if}}
            </div>
          </:body>
          <:footer>
            <div class="flex justify-end gap-3">
              <Button @appearance="outlined" {{on "click" this.closeSelectModal}}>Cancel</Button>
              <Button @intent="primary" {{on "click" this.confirmSelectModal}}>
                Assign Reviewer
              </Button>
            </div>
          </:footer>
        </Modal>
      </div>

      {{! ================================================================
            SECTION 2: ConfirmationDialog
            ================================================================ }}
      <SectionHeader
        @title="ConfirmationDialog"
        @icon={{PhChatCircle}}
        @subtitle="Intent-colored confirmation with glassmorphism header, animated icons, shake-on-error, and type-to-confirm."
      />

      <div class="space-y-6">
        {{! Intent Variants }}
        <div class="bg-white rounded-xl border border-gray-200 p-6">
          <Heading @level={{4}} class="mb-2">Intent Variants</Heading>
          <Text @intent="muted" class="mb-4">Each intent has its own gradient header, icon, and
            color scheme.</Text>
          <div class="flex flex-wrap gap-3">
            <Button @intent="warning" {{on "click" (fn this.openConfirm "warning")}}>
              <PhWarning @size={{16}} class="mr-1" />
              Warning
            </Button>
            <Button @intent="danger" {{on "click" (fn this.openConfirm "danger")}}>
              <PhTrash @size={{16}} class="mr-1" />
              Danger
            </Button>
            <Button @intent="success" {{on "click" (fn this.openConfirm "success")}}>
              <PhCheckCircle @size={{16}} class="mr-1" />
              Success
            </Button>
            <Button @appearance="outlined" {{on "click" (fn this.openConfirm "info")}}>
              <PhInfo @size={{16}} class="mr-1" />
              Info
            </Button>
          </div>
        </div>

        {{! Warning Dialog }}
        <ConfirmationDialog
          @isOpen={{this.isWarningConfirmOpen}}
          @title="Unsaved Changes"
          @intent="warning"
          @confirmText="Discard"
          @onConfirm={{fn this.handleConfirm "warning"}}
          @onCancel={{fn this.closeConfirm "warning"}}
        >
          You have unsaved changes that will be lost. This action cannot be undone.
        </ConfirmationDialog>

        {{! Danger Dialog }}
        <ConfirmationDialog
          @isOpen={{this.isDangerConfirmOpen}}
          @title="Delete Account"
          @intent="danger"
          @confirmText="Delete Forever"
          @onConfirm={{fn this.handleConfirm "danger"}}
          @onCancel={{fn this.closeConfirm "danger"}}
        >
          This will permanently delete your account and all associated data. There is no way to
          recover this.
        </ConfirmationDialog>

        {{! Success Dialog }}
        <ConfirmationDialog
          @isOpen={{this.isSuccessConfirmOpen}}
          @title="Approve Application"
          @intent="success"
          @confirmText="Approve"
          @onConfirm={{fn this.handleConfirm "success"}}
          @onCancel={{fn this.closeConfirm "success"}}
        >
          This application meets all requirements. Approve to release funds to the employee.
        </ConfirmationDialog>

        {{! Info Dialog }}
        <ConfirmationDialog
          @isOpen={{this.isInfoConfirmOpen}}
          @title="Enable Feature"
          @intent="info"
          @confirmText="Enable"
          @onConfirm={{fn this.handleConfirm "info"}}
          @onCancel={{fn this.closeConfirm "info"}}
        >
          This will enable the beta feature for your organization. You can disable it anytime from
          settings.
        </ConfirmationDialog>

        {{! Type-to-Confirm }}
        <div class="bg-white rounded-xl border border-gray-200 p-6">
          <Heading @level={{4}} class="mb-2">Type-to-Confirm</Heading>
          <Text @intent="muted" class="mb-4">Requires the user to type a phrase before the confirm
            button activates. Glow ring animates on match.</Text>
          <Button @intent="danger" {{on "click" this.openTypeConfirm}}>
            <PhTrash @size={{16}} class="mr-1" />
            Delete with Confirmation
          </Button>
        </div>

        <ConfirmationDialog
          @isOpen={{this.isTypeConfirmOpen}}
          @title="Delete All Data"
          @intent="danger"
          @confirmText="Delete Everything"
          @typeToConfirm="delete my data"
          @typeToConfirmHint="delete my data"
          @onConfirm={{this.handleTypeConfirm}}
          @onCancel={{this.closeTypeConfirm}}
        >
          This will permanently erase all records. Type the phrase below to proceed.
        </ConfirmationDialog>

        {{! Loading State }}
        <div class="bg-white rounded-xl border border-gray-200 p-6">
          <Heading @level={{4}} class="mb-2">Loading State</Heading>
          <Text @intent="muted" class="mb-4">Simulate async operations — button shows spinner and
            dialog stays open until complete.</Text>
          <Button @intent="primary" {{on "click" this.openLoadingConfirm}}>
            <PhLightning @size={{16}} class="mr-1" />
            Async Confirmation
          </Button>
        </div>

        <ConfirmationDialog
          @isOpen={{this.isLoadingConfirmOpen}}
          @title="Process Payment"
          @intent="info"
          @confirmText="Process Now"
          @isRunning={{this.isConfirmRunning}}
          @error={{this.confirmError}}
          @onConfirm={{this.handleLoadingConfirm}}
          @onCancel={{this.closeLoadingConfirm}}
        >
          This will initiate the payment processing pipeline. The operation takes a few seconds.
        </ConfirmationDialog>
      </div>

      {{! ================================================================
            SECTION 3: Toast Notifications
            ================================================================ }}
      <SectionHeader
        @title="Toast Notifications"
        @icon={{PhBell}}
        @subtitle="Frosted-glass toast cards with confetti, progress bars, and slide-in animations. Managed via the reimagined-toast service."
      />

      <div class="space-y-6">
        {{! Intent Variants }}
        <div class="bg-white rounded-xl border border-gray-200 p-6">
          <Heading @level={{4}} class="mb-2">Intent Variants</Heading>
          <Text @intent="muted" class="mb-4">Each toast intent has unique colors, icons, and
            behavior. Success toasts include a confetti burst!</Text>
          <div class="flex flex-wrap gap-3">
            <Button @intent="success" {{on "click" this.fireSuccessToast}}>
              <PhConfetti @size={{16}} class="mr-1" />
              Success Toast
            </Button>
            <Button @intent="danger" {{on "click" this.fireErrorToast}}>
              Error Toast
            </Button>
            <Button @intent="warning" {{on "click" this.fireWarningToast}}>
              Warning Toast
            </Button>
            <Button @appearance="outlined" {{on "click" this.fireInfoToast}}>
              <PhInfo @size={{16}} class="mr-1" />
              Info Toast
            </Button>
          </div>
        </div>

        {{! With Action Button }}
        <div class="bg-white rounded-xl border border-gray-200 p-6">
          <Heading @level={{4}} class="mb-2">With Action</Heading>
          <Text @intent="muted" class="mb-4">Toasts can include an action button for quick follow-up
            actions.</Text>
          <Button @intent="primary" {{on "click" this.fireActionToast}}>
            <PhBell @size={{16}} class="mr-1" />
            Toast with Action
          </Button>
        </div>

        {{! Burst }}
        <div class="bg-white rounded-xl border border-gray-200 p-6">
          <Heading @level={{4}} class="mb-2">Toast Burst</Heading>
          <Text @intent="muted" class="mb-4">Multiple toasts stack with staggered entrance
            animations.</Text>
          <Button @intent="primary" {{on "click" this.fireBurstToast}}>
            <PhLightning @size={{16}} class="mr-1" />
            Fire Burst (3 toasts)
          </Button>
        </div>

        {{! API Reference }}
        <div class="bg-gray-50 rounded-xl border border-gray-200 p-6">
          <Heading @level={{4}} class="mb-3">Service API</Heading>
          <div class="font-mono text-sm space-y-2 text-gray-700">
            <div><span class="text-violet-600">this.toast</span>.success(<span
                class="text-green-700"
              >'message'</span>)</div>
            <div><span class="text-violet-600">this.toast</span>.error(<span
                class="text-green-700"
              >'message'</span>)</div>
            <div><span class="text-violet-600">this.toast</span>.warning(<span
                class="text-green-700"
              >'message'</span>)</div>
            <div><span class="text-violet-600">this.toast</span>.info(<span
                class="text-green-700"
              >'message'</span>)</div>
            <div><span class="text-violet-600">this.toast</span>.success(<span
                class="text-green-700"
              >'msg'</span>, {'{'} actionLabel:
              <span class="text-green-700">'View'</span>, onAction: fn {'}'})</div>
            <div><span class="text-violet-600">this.toast</span>.dismiss(<span
                class="text-green-700"
              >'id'</span>)</div>
            <div><span class="text-violet-600">this.toast</span>.clear()</div>
          </div>
        </div>
      </div>

      {{! ================================================================
            Summary
            ================================================================ }}
      <div class="bg-violet-50 rounded-xl p-6 border border-violet-100 mt-8">
        <div class="flex items-start gap-4">
          <div
            class="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center shrink-0"
          >
            <PhStack @size={{24}} class="text-violet-600" />
          </div>
          <div>
            <Heading @level={{3}} class="text-violet-900 mb-2">Overlay Components</Heading>
            <Text @intent="muted" class="mb-4">
              Three zero-Frontile overlay components with premium polish: frosted backdrops,
              spring-feel animations, glassmorphism, confetti, and accessible keyboard/focus
              management.
            </Text>
            <div class="flex flex-wrap gap-2">
              <Chip @intent="primary" @size="sm">Modal</Chip>
              <Chip @intent="primary" @size="sm">ConfirmationDialog</Chip>
              <Chip @intent="primary" @size="sm">Toast</Chip>
            </div>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}
